/**
 * React Router v6 Compatibility Layer
 *
 * This file provides compatibility utilities for migrating from react-router v4/v5 to v6.
 * It recreates the `withRouter` HOC and provides typed hooks for navigation.
 */

import React, { ComponentType } from 'react';
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
  NavigateFunction,
  Location,
  Params
} from 'react-router-dom';

// Types for the injected router props (matching v4/v5 API)
export interface RouterProps {
  history: {
    push: (path: string, state?: any) => void;
    replace: (path: string, state?: any) => void;
    go: (n: number) => void;
    goBack: () => void;
    goForward: () => void;
    location: Location;
    listen: (callback: (location: Location) => void) => () => void;
  };
  location: Location;
  match: {
    params: Params<string>;
    path: string;
    url: string;
    isExact: boolean;
  };
  navigate: NavigateFunction;
}

/**
 * withRouter HOC - Compatibility wrapper for v6
 *
 * This recreates the v4/v5 withRouter behavior using v6 hooks.
 * Components wrapped with this will receive history, location, and match props.
 *
 * Usage:
 * ```
 * import { withRouter } from '../utils/routerCompat';
 *
 * class MyComponent extends React.Component<Props & RouterProps> {
 *   handleClick = () => {
 *     this.props.history.push('/new-path');
 *   }
 * }
 *
 * export default withRouter(MyComponent);
 * ```
 */
export function withRouter<P extends object>(
  WrappedComponent: ComponentType<P & RouterProps>
): ComponentType<Omit<P, keyof RouterProps>> {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithRouter = (props: Omit<P, keyof RouterProps>) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const [searchParams] = useSearchParams();

    // Create a history-like object that mimics v4/v5 API
    const history = React.useMemo(() => ({
      push: (path: string, state?: any) => navigate(path, { state }),
      replace: (path: string, state?: any) => navigate(path, { replace: true, state }),
      go: (n: number) => navigate(n),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      location,
      // Note: listen is not fully supported in v6, but we provide a stub
      listen: (callback: (location: Location) => void) => {
        // In v6, you should use useEffect with location dependency instead
        console.warn('history.listen() is deprecated in react-router v6. Use useEffect with location dependency.');
        return () => {};
      }
    }), [navigate, location]);

    // Create a match-like object
    const match = React.useMemo(() => ({
      params,
      path: location.pathname,
      url: location.pathname,
      isExact: true // In v6, all matches are exact by default
    }), [params, location.pathname]);

    const routerProps: RouterProps = {
      history,
      location,
      match,
      navigate
    };

    return <WrappedComponent {...(props as P)} {...routerProps} />;
  };

  ComponentWithRouter.displayName = `withRouter(${displayName})`;

  return ComponentWithRouter;
}

/**
 * Custom hook to get navigation functions with v4/v5-like API
 */
export function useRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const history = React.useMemo(() => ({
    push: (path: string, state?: any) => navigate(path, { state }),
    replace: (path: string, state?: any) => navigate(path, { replace: true, state }),
    go: (n: number) => navigate(n),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    location
  }), [navigate, location]);

  const match = React.useMemo(() => ({
    params,
    path: location.pathname,
    url: location.pathname,
    isExact: true
  }), [params, location.pathname]);

  return { history, location, match, navigate, params };
}

/**
 * Hook to get query parameters as an object
 */
export function useQueryParams(): Record<string, string> {
  const [searchParams] = useSearchParams();
  return React.useMemo(() => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [searchParams]);
}

// Re-export v6 hooks for convenience
export { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
